import "./CourseUsersSidebarPanel.scss";

import clsx from "clsx";
import type { FC } from "react";
import { MouseEvent, useCallback, useEffect, useMemo, useState } from "react";
import { useCopyToClipboard } from "usehooks-ts";

import { CloseOverlayButton } from "../sidebar/CloseOverlayButton";
import { ContentSidebarHeader } from "../sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "../sidebar/ContentSidebarMain";
import { OverlayContainer } from "../sidebar/OverlayContainer";

import { CourseRoleCard } from "./CourseRoleCard";
import { DisabledUsersCard } from "./CourseUserCard";

import {
  serverCreateCourseInvite,
  serverDeleteCourseInvite,
  serverGetCourseInvite,
} from "api/api-server-actions";
import { LoadingScreen } from "components/LoadingScreen";
import type { CourseInviteType } from "components/server-types";
import { getSidebarHeader } from "helpers/sidebar";
import { t } from "i18n/i18n";
import { Button } from "mds/components/Button";
import { IconText } from "mds/components/IconText";
import { LegacyButton } from "mds/components/LegacyButton";
import { Menu, MenuItem } from "mds/components/Menu";
import { showConfirmationModal } from "mds/components/Modal";
import { TextField } from "mds/components/TextField";
import { Tooltip } from "mds/components/Tooltip";
import {
  AddPlusIcon,
  DotMenuVerticalIcon,
  DuplicateCopyIcon,
  LongFormPencilIcon,
  PersonClassIcon,
  StarIcon,
  TrashDeleteIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectCurrentCourseId,
  selectCurrentUserId,
  selectEnhancedUsersWithCourseActivityInCurrentCourse,
  selectOrgUserByUserIdMap,
} from "store/selectors";
import { useListSelector, useObjectSelector } from "store/store-hooks";
import { toastCreateOperationCatcher, toastLocalizedOperationCatcher } from "utils/alerts";
import { CONTENT_SIDEBAR_TAB_NAME } from "utils/urls";

export const CourseUsersSidebarPanel = () => {
  const courseId = useAppSelector(selectCurrentCourseId);
  const users = useListSelector(selectEnhancedUsersWithCourseActivityInCurrentCourse);
  const { canAuthorCourse, canAuthorOrg } = useCapabilities();
  const orgUserMap = useObjectSelector(selectOrgUserByUserIdMap);
  // This makes sure an org admin that's a student in the course can then change themselves back to an instructor
  const canModifyUsers = canAuthorCourse || canAuthorOrg;
  const userId = useAppSelector(selectCurrentUserId);

  const currentUserInThisCourse = useMemo(
    () => users.find((user) => user.user_id === userId),
    [users, userId],
  );

  const canAddSelfToCourse = canAuthorOrg && !currentUserInThisCourse;

  const onJoinAsInstructor = useCallback(async () => {
    try {
      const courseUser = await storeApi.course_users.create(
        {
          course_id: courseId,
          user_id: userId,
          role: "instructor",
        },
        { skipToast: true },
      );
      await storeApi.class_users.create({
        course_id: courseId,
        course_user_id: courseUser.id,
      });
    } catch (error) {
      toastLocalizedOperationCatcher("join_as_instructor")(error as Error);
    }
  }, [courseId, userId]);

  const enabledUsers = useMemo(
    () =>
      users.filter((user) => {
        if (user.is_disabled) {
          return false;
        }
        const orgUser = orgUserMap?.[user.user_id];
        if (!orgUser) {
          return true;
        }
        return !orgUser.is_disabled;
      }),
    [users, orgUserMap],
  );

  const disabledUsers = useMemo(
    () =>
      users.filter((user) => {
        if (user.is_disabled) {
          return true;
        }
        const orgUser = orgUserMap?.[user.user_id];
        if (!orgUser) {
          return false;
        }
        return orgUser.is_disabled;
      }),
    [users, orgUserMap],
  );

  const emailAddresses = useMemo(() => {
    const otherUsers = enabledUsers.filter((user) => user.user_id !== userId);
    return otherUsers
      .map((user) => user.email)
      .filter((email) => email) // Filter out any undefined emails
      .join(",");
  }, [enabledUsers, userId]);

  if (!courseId) {
    return <LoadingScreen />;
  }

  const canSeeCourseAdmins =
    (canAuthorCourse || canAuthorOrg) && users.some((u) => u.role === "course_admin");

  return (
    <OverlayContainer>
      <ContentSidebarHeader panelType="overlay" visibleOnMobile>
        <IconText
          iconStart={<PersonClassIcon className="text-blue" />}
          text={getSidebarHeader(CONTENT_SIDEBAR_TAB_NAME.Users)}
        />

        <div className="flex items-center justify-end gap-2">
          {canModifyUsers && (
            <Menu
              direction="bottom"
              menuButton={
                <Button
                  className="px-1"
                  eventName="Course Users - Options"
                  kind="white"
                  size="xs"
                  title={t("common.options")}
                  onClick={(e: MouseEvent) => {
                    e.preventDefault();
                    e.stopPropagation();
                  }}
                >
                  <DotMenuVerticalIcon />
                </Button>
              }
              menuClassName="min-w-[180px]"
            >
              <MenuItem
                eventName="Course Users - Add students"
                onClick={() => onShowInviteStudentModal({ courseId })}
              >
                <IconText
                  iconStart={<AddPlusIcon />}
                  text={t("user_options_menu.invite_students.button")}
                />
              </MenuItem>

              {emailAddresses && (
                <MenuItem
                  eventName="Course users - Email all"
                  href={`mailto:${emailAddresses}`}
                  target="_blank"
                  allowPropagation
                >
                  <IconText
                    iconStart={<LongFormPencilIcon />}
                    text={t("user_options_menu.email_all")}
                  />
                </MenuItem>
              )}
            </Menu>
          )}

          <CloseOverlayButton size="xs" />
        </div>
      </ContentSidebarHeader>

      <ContentSidebarMain bottomPadding={false} className="course-user-sidebar-panel">
        {canAddSelfToCourse && (
          <Button
            className="mb-4 flex items-center gap-2"
            kind="white"
            size="xs"
            onClick={onJoinAsInstructor}
          >
            <span>{t("user_options_menu.join_as_instructor.title")}</span>
            <Tooltip text={t("user_options_menu.join_as_instructor.tooltip")} />
          </Button>
        )}

        <CourseRoleCard
          canModifyUsers={canModifyUsers}
          className="mb-4 mt-2"
          courseRole="instructor"
          headerIcon={<StarIcon size="xs" />}
          users={enabledUsers}
        />

        {canSeeCourseAdmins && (
          <CourseRoleCard
            canModifyUsers={canModifyUsers}
            className="mb-4 mt-2"
            courseRole="course_admin"
            users={enabledUsers}
          />
        )}

        <CourseRoleCard
          canModifyUsers={canModifyUsers}
          className="mb-4"
          courseRole="student"
          users={enabledUsers}
          separateUsersInClassSession
          showEmpty
          showOnlineCount
          startOpen
        />

        {canModifyUsers && <DisabledUsersCard className="mb-4" users={disabledUsers} />}
      </ContentSidebarMain>
    </OverlayContainer>
  );
};

interface InviteStudentModalProps {
  courseId: string;
}

const onShowInviteStudentModal = (props: InviteStudentModalProps) => {
  showConfirmationModal({
    title: t("user_options_menu.invite_students.title"),
    hideCancelConfirmButtons: true,
    children: (onClose) => <InviteStudentModal {...props} onClose={onClose} />,
  }).catch(toastLocalizedOperationCatcher("show_student_invite_modal"));
};

export const InviteStudentModal: FC<InviteStudentModalProps & { onClose: () => void }> = ({
  courseId,
  onClose,
}) => {
  const [recentlyCopied, setRecentlyCopied] = useState(false);
  // We separately have controls to hide the modal, because we may need to show the delete invite modal
  // _while_ showing this modal
  const [hideModal, setHideModal] = useState(false);

  const [courseInvite, setCourseInvite] = useState<CourseInviteType | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const [, copyToClipboard] = useCopyToClipboard();

  const link = `${window.location.origin}/?invite_key=${courseInvite?.invite_key}`;

  // TODO: Replace with storeApi
  useEffect(() => {
    serverGetCourseInvite(courseId)
      // We assume there's only one invite per course right now
      .then((invites) => setCourseInvite(invites[0]))
      .catch(setError)
      .finally(() => setLoading(false));
  }, [courseId]);

  const onDeleteInvite = async () => {
    await serverDeleteCourseInvite(courseInvite.id);
    setCourseInvite(null);
  };

  const onCreateInvite = async () => {
    const invite = await serverCreateCourseInvite(courseId).catch(
      toastCreateOperationCatcher("invite"),
    );

    setCourseInvite(invite || null);
  };

  const onCopy = async () => {
    await copyToClipboard(link);
    setRecentlyCopied(true);
    setTimeout(() => setRecentlyCopied(false), 2000);
  };

  if (error) {
    return (
      <div className="course-invite-modal px-6 pb-6">
        <h3 className="my-2">{t("user_options_menu.invite_students.error_title")}</h3>
        <div className="body-s">
          {t("user_options_menu.invite_students.error_body", { error_message: error?.message })}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="course-invite-modal px-6 pb-6">
        <LoadingScreen />
      </div>
    );
  }

  if (!courseInvite) {
    return (
      <div className="course-invite-modal flex flex-col items-center gap-5">
        <div className="body-s">{t("user_options_menu.invite_students.explanation")}</div>
        <div className="flex w-full justify-end gap-2">
          <LegacyButton kind="secondary" onClick={onClose}>
            {t("common.cancel")}
          </LegacyButton>
          <LegacyButton eventName="New invitation modal - Confirm" onClick={() => onCreateInvite()}>
            {t("user_options_menu.invite_students.action_create")}
          </LegacyButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className={clsx("course-invite-modal flex flex-col items-start", {
        hidden: hideModal,
      })}
    >
      <div className="body-s course-invite-modal-text mb-6">
        {t("user_options_menu.invite_students.label")}
      </div>

      <div className="my-3 mb-6 flex w-full justify-center">
        <img
          alt={t("user_options_menu.invite_students.qr_code_alt_text")}
          className="course-invite-modal-qr-code"
          src={`https://api.qrserver.com/v1/create-qr-code/?size=128x128&data=${link}`}
        />
      </div>

      <div className="mt-3 flex items-center gap-2">
        <LegacyButton
          eventName="Delete student invitation link"
          kind="destructive"
          title={t("user_options_menu.invite_students.action_delete")}
          iconOnly
          onClick={() => {
            setHideModal(true);
            showConfirmationModal({
              title: t("user_options_menu.invite_students.delete_title"),
              labelText: t("user_options_menu.invite_students.warning_delete"),
              confirmButtonText: t("user_options_menu.invite_students.action_delete"),
              confirmButtonKind: "destructive",
              onClose: () => {
                setHideModal(false);
              },
              onConfirm: () => {
                onDeleteInvite();
                onClose();
              },
            });
          }}
        >
          <TrashDeleteIcon />
        </LegacyButton>

        <TextField className="course-invite-modal-link" value={link} readOnly />

        <div className="course-invite-copy flex items-center">
          <Tooltip
            text={
              recentlyCopied
                ? t("user_options_menu.invite_students.copy_link_success")
                : t("user_options_menu.invite_students.copy_link")
            }
          >
            <LegacyButton
              eventName="Copy student invitation link"
              kind="primary"
              title={t("user_options_menu.invite_students.copy_link")}
              iconOnly
              onClick={onCopy}
            >
              <IconText
                iconStart={<DuplicateCopyIcon />}
                text={t("user_options_menu.invite_students.copy_link")}
              />
            </LegacyButton>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};
